import { createElement, useId } from 'react';
import type { ChangeEvent } from 'react';

export interface TimezoneOption {
  value: string;
  label: string;
  offset: string;
}

export const TIMEZONE_OPTIONS: TimezoneOption[] = [
  { value: 'UTC', label: 'UTC', offset: '+00:00' },
  { value: 'America/New_York', label: 'US Eastern (New York)', offset: '-05:00' },
  { value: 'America/Chicago', label: 'US Central (Chicago)', offset: '-06:00' },
  { value: 'America/Denver', label: 'US Mountain (Denver)', offset: '-07:00' },
  { value: 'America/Los_Angeles', label: 'US Pacific (Los Angeles)', offset: '-08:00' },
  { value: 'Europe/London', label: 'Europe London', offset: '+00:00' },
  { value: 'Europe/Berlin', label: 'Europe Berlin', offset: '+01:00' },
  { value: 'Europe/Stockholm', label: 'Europe Stockholm', offset: '+01:00' },
  { value: 'Asia/Tokyo', label: 'Asia Tokyo', offset: '+09:00' },
  { value: 'Asia/Singapore', label: 'Asia Singapore', offset: '+08:00' },
  { value: 'Australia/Sydney', label: 'Australia Sydney', offset: '+10:00' },
  { value: 'Pacific/Auckland', label: 'Pacific Auckland', offset: '+12:00' },
];

const GROUP_LABELS: Record<string, string> = {
  UTC: 'UTC',
  America: 'North & South America',
  Europe: 'Europe',
  Asia: 'Asia',
  Australia: 'Australia',
  Pacific: 'Pacific',
};

function getTimezoneGroup(value: string): string {
  if (value === 'UTC') return 'UTC';
  return value.split('/')[0] ?? 'Other';
}

interface TimeZoneSelectProps {
  value: string;
  onChange: (value: string) => void;
  id?: string;
  label?: string;
  className?: string;
  disabled?: boolean;
}

export function TimeZoneSelect({ value, onChange, id, label, className, disabled }: TimeZoneSelectProps) {
  const generatedId = useId();
  const selectId = id ?? `timezone-${generatedId}`;

  const grouped = TIMEZONE_OPTIONS.reduce<Record<string, TimezoneOption[]>>((acc, option) => {
    const group = getTimezoneGroup(option.value);
    const groupOptions = acc[group] ?? [];
    groupOptions.push(option);
    acc[group] = groupOptions;
    return acc;
  }, {});

  const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
    onChange(event.target.value);
  };

  return createElement('div', { className: 'flex flex-col gap-2' }, [
    label
      ? createElement(
        'label',
        { key: 'label', htmlFor: selectId, className: 'text-sm font-medium text-slate-700 dark:text-slate-200' },
        label,
      )
      : null,
    createElement(
      'select',
      {
        key: 'select',
        id: selectId,
        value,
        onChange: handleChange,
        className: className
          ?? 'block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100',
        disabled,
      },
      Object.entries(grouped).map(([group, options]) =>
        createElement(
          'optgroup',
          { key: group, label: GROUP_LABELS[group] ?? group },
          options.map((option) =>
            createElement(
              'option',
              { key: option.value, value: option.value },
              `(UTC${option.offset}) ${option.label}`,
            ),
          ),
        ),
      ),
    ),
  ]);
}
